/* eslint-disable react/require-default-props */
import React from 'react';

import { CF_CRT_MED } from '../../constants';

import BarSegmentRenderer from './BarSegmentRenderer';
import type { GradientConfig, SegmentData } from './types';

/**
 * Props for BarGroupRenderer component
 */
export interface BarGroupRendererProps {
  /** Array of segment data for this bar group */
  segments: SegmentData[];
  /** X position of the bar group slot start */
  slotX: number;
  /** Width of the entire slot (bar spacing) */
  slotWidth: number;
  /** Width of the bar (60% of slot) */
  barWidth: number;
  /** Y position of the chart top (for hover target height) */
  chartTop: number;
  /** Y position of the chart bottom (baseline) */
  chartBottom: number;
  /** Height of the chart area */
  chartHeight: number;
  /** Animation progress (0 to 1) */
  animationProgress: number;
  /** Whether this bar group is currently hovered */
  isHovered?: boolean;
  /** Bar index for identifying this bar group */
  barIndex?: number;
  /** Label/identifier for accessibility */
  label?: string;
  /** Overlay metric value (e.g., percentage) */
  overlayValue?: number;
  /** Overlay metric formatter function */
  overlayFormatter?: (value: number) => string;
  /** Overlay metric color */
  overlayColor?: string;
  /** Callback when mouse enters the bar group */
  onMouseEnter?: () => void;
  /** Callback when mouse leaves the bar group */
  onMouseLeave?: () => void;
  /** Callback when bar group is clicked */
  onClick?: () => void;
  /** Callback when segment is hovered */
  onSegmentHover?: (segmentId: string, isHovered: boolean) => void;
  /** Optional CSS class name */
  className?: string;
  /** Optional gradient configurations for segments */
  segmentGradients?: GradientConfig[];
}
const BarGroupRenderer = React.memo(
  ({
    segments,
    slotX,
    slotWidth,
    barWidth,
    chartTop,
    chartBottom,
    chartHeight,
    animationProgress,
    isHovered = false,
    barIndex,
    label,
    overlayValue,
    overlayFormatter,
    overlayColor,
    onMouseEnter,
    onMouseLeave,
    onClick,
    onSegmentHover,
    className,
    segmentGradients,
  }: BarGroupRendererProps) => {
    const barX = slotX + (slotWidth - barWidth) / 2;
    const totalBarHeight =
      segments.reduce((sum, seg) => sum + seg.height, 0) * animationProgress;
    const overlayY = chartBottom - totalBarHeight - 15; // 15px above the bar
    const barCenterX = barX + barWidth / 2;

    return (
      <g
        className={`BarGroupRenderer ${className ?? ''}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        role='presentation'>
        <rect
          x={slotX}
          y={chartTop}
          width={slotWidth}
          height={chartHeight}
          fill='transparent'
          pointerEvents='all'
          className='BarGroupRenderer-hoverTarget'
          aria-hidden='true'
        />
        <g className='BarGroupRenderer-segments' role='presentation'>
          {segments.map((segment, segmentIndex) => {
            const isTopSegment = segmentIndex === segments.length - 1;
            const segmentHeight = segment.height * animationProgress;
            const segmentY =
              chartBottom - segment.cumulativeHeight - segmentHeight;

            // Find gradient config for this segment
            const gradient = segmentGradients?.find(
              (g) => g.categoryId === segment.categoryId
            );

            return (
              <BarSegmentRenderer
                key={`segment-${segment.categoryId}`}
                segment={segment}
                x={barX}
                y={segmentY}
                width={barWidth}
                height={segmentHeight}
                isTopSegment={isTopSegment}
                isHovered={isHovered}
                label={label}
                barIndex={barIndex}
                gradient={gradient}
                onMouseEnter={() => onSegmentHover?.(segment.categoryId, true)}
                onMouseLeave={() => onSegmentHover?.(segment.categoryId, false)}
                className='BarGroupRenderer-segment'
              />
            );
          })}
        </g>

        {overlayValue !== undefined && overlayFormatter && (
          <text
            x={barCenterX}
            y={overlayY}
            textAnchor='middle'
            dominantBaseline='middle'
            fontSize='12'
            fontWeight='600'
            fill={overlayColor ?? `var(${CF_CRT_MED})`}
            pointerEvents='none'
            className='BarGroupRenderer-overlayLabel'>
            {overlayFormatter(overlayValue)}
          </text>
        )}
      </g>
    );
  }
);

BarGroupRenderer.displayName = 'BarGroupRenderer';

export default BarGroupRenderer;
